import styled from 'styled-components';
import { useQuery, useMutation } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Main as Container } from 'Components/Main';
import { Dialog, PageHeader } from 'Components/ui';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { getProcessById, deleteProcessMapping } from 'services/processes-service';
import { getLegalBases, getDataCategories } from 'services/mapping-service';
import { useAuthTenant } from 'store/auth';
import { ShowHeaderBtnSecondary } from '../../styles';

const BlueOutlineBtn = styled(ShowHeaderBtnSecondary)`
  color: var(--vo-celeste);
  border-color: var(--vo-celeste);
  &:hover { background: color-mix(in srgb, var(--vo-info) 6%, transparent); }
`;

const DangerBtn = styled(ShowHeaderBtnSecondary)`
  color: var(--vo-danger);
  border-color: var(--vo-danger);
  &:hover { background: color-mix(in srgb, var(--vo-danger) 6%, transparent); }
`;

/* ── Content wrapper ── */
const Wrap = styled.div`max-width: 860px;`;

/* ── Process context card ── */
const ProcCard = styled.div`
  background: var(--vo-treatment-collect-grad);
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const ProcIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--vo-cobalto) 15%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const ProcLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-cobalto);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 3px;
  opacity: .7;
`;
const ProcName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: var(--vo-ink);
  margin: 0 0 10px;
  letter-spacing: -.01em;
`;
const MetaBadgeWrap = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const MetaBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${p => p.$bg || 'color-mix(in srgb, var(--vo-cobalto) 10%, transparent)'};
  border-radius: 8px;
  padding: 6px 12px;
`;
const MetaBadgeText = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color || 'var(--vo-cobalto)'};
`;
const DescBox = styled.div`
  background: var(--vo-cobalto-8);
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 14px;
`;
const DescLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-cobalto);
  margin: 0 0 4px;
  opacity: .8;
`;
const DescText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0;
  line-height: 1.6;
`;

/* ── Dado card ── */
const DadoCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;
const DadoHeader = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, var(--vo-capri-100) 0%, var(--vo-capri-200) 100%);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;
const DadoName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: var(--vo-ink);
  margin: 0 0 8px;
`;
const CatBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: var(--vo-info-15);
  color: var(--vo-celeste);
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 12px;
  border-radius: 20px;
`;
const TreatCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: color-mix(in srgb, var(--vo-success) 12%, transparent);
  color: var(--vo-success);
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 20px;
  flex-shrink: 0;
  margin-top: 4px;
`;

/* ── 4-col info strip ── */
const InfoStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: var(--vo-treatment-collect-bg);
  padding: 16px 24px;
  gap: 0;
`;
const InfoCell = styled.div`padding: 8px 12px;`;
const InfoLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 5px;
`;
const InfoValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink);
  margin: 0;
  font-weight: 500;
`;

/* ── Treatment sections ── */
const TreatSection = styled.div`border-top: 1px solid var(--vo-cloud);`;
const TreatHdr = styled.div`
  padding: 12px 24px;
  background: ${p => p.$grad};
  display: flex;
  align-items: center;
  gap: 8px;
`;
const TreatTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.$color};
  margin: 0;
`;
const TreatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 16px 24px;
  background: ${p => p.$bg};
  gap: 0;
`;
const TreatCell = styled.div`
  padding: 8px 12px;
  ${p => p.$full && 'grid-column: 1 / -1;'}
  ${p => p.$spanTwo && 'grid-column: 2 / -1;'}
  ${p => p.$topBorder && `border-top: 1px solid ${p.$borderColor || 'var(--vo-cloud)'}; margin-top: 4px;`}
`;

/* ── Field label / value ── */
const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 5px;
`;
const FieldValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink);
  margin: 0;
`;
const FieldValueMuted = styled(FieldValue)`color: var(--vo-slate);`;

/* ── SVGs ── */
const EditSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const LayersSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--vo-cobalto)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
    <polyline points="2 17 12 22 22 17"/>
    <polyline points="2 12 12 17 22 12"/>
  </svg>
);
const UserSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--vo-cobalto)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const ShieldSvg = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

/* ── Constants ── */
const CRIT_STYLES = {
  '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
  '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
  '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
  '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
  '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
};

const TREAT_COLORS = [
  { grad: 'var(--vo-treatment-store-grad)', text: 'var(--vo-treatment-store)', bg: 'var(--vo-treatment-store-bg)', border: 'var(--vo-treatment-store-border)' },
  { grad: 'var(--vo-treatment-share-grad)', text: 'var(--vo-treatment-share)', bg: 'var(--vo-treatment-share-bg)', border: 'var(--vo-treatment-share-border)' },
  { grad: 'var(--vo-treatment-collect-grad)', text: 'var(--vo-treatment-collect)', bg: 'var(--vo-treatment-collect-bg)', border: 'var(--vo-treatment-collect-border)' },
  { grad: 'var(--vo-treatment-purple-grad)', text: 'var(--vo-treatment-purple)', bg: 'var(--vo-treatment-purple-bg)', border: 'var(--vo-treatment-purple-border)' },
  { grad: 'var(--vo-treatment-gray-grad)', text: 'var(--vo-ink-2)', bg: 'var(--vo-mist)', border: 'var(--vo-line)' },
];

export const ProcessesDatasShow = () => {
  const { t, i18n } = useTranslation('mapping');
  const { id, mappingId } = useParams();
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const { isLoading, error, data: process } = useQuery('getProcessById', async () => {
    const res = await getProcessById(id);
    return res.status === 200 ? res.data : null;
  });

  const { data: legalBasesList } = useQuery(
    ['getLegalBases', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getLegalBases(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const { data: dataCategoriesList } = useQuery(
    ['getDataCategories', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getDataCategories(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );

  const deleteMutation = useMutation(
    () => deleteProcessMapping(id, mappingId),
    {
      onSuccess: () => {
        Dialog.fire({
          title: t('processes.toasts.successTitle'),
          text: t('processes.datas.deleteSuccess'),
          icon: 'success',
          didClose: () => navigate(`/processes/show/${id}`),
        });
      },
      onError: err => {
        Dialog.alert({
          title: t('processes.toasts.errorTitle'),
          message: err.response?.data?.message ?? err.message,
          icon: 'error',
        });
      },
    },
  );

  const handleDelete = async () => {
    const confirmed = await Dialog.confirm({
      title: t('processes.show.modals.deleteDataMessage', { name: dado?.name ?? '' }),
      confirmLabel: t('processes.datas.deleteBtn'),
      confirmColor: 'var(--vo-danger)',
    });
    if (confirmed) deleteMutation.mutate();
  };

  const legalBaseByKey = new Map((legalBasesList ?? []).map(lb => [lb.key, lb.name]));
  const categoryByKey  = new Map((dataCategoriesList ?? []).map(c  => [c.key, c.name]));

  if (isLoading) return <PageLoaderShow />;
  if (error) return <div>{t('processes.datas.errorLoad')}</div>;

  const dado       = process?.dataMapping?.[mappingId];
  const treatments = dado?.dataProcessing ?? [];

  const critStyle = CRIT_STYLES[process?.criticity] ?? { color: 'var(--vo-slate)', bg: 'var(--vo-slate-10)' };
  const critText  = {
    '1': t('processes.criticality.short.1'),
    '2': t('processes.criticality.short.2'),
    '3': t('processes.criticality.short.3'),
    '4': t('processes.criticality.short.4'),
    '5': t('processes.criticality.short.5'),
  }[process?.criticity];

  const categoryName = dado?.category === 'other'
    ? (dado?.customDataCategory ?? dado?.category)
    : (categoryByKey.get(dado?.category) ?? dado?.category ?? '—');

  return (
    <Container>
      <PageHeader
        title={dado?.name ?? process?.name}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('processes.list.title'), route: '/processes' },
          { label: process?.name, route: `/processes/show/${id}` },
          { label: t('processes.datas.breadcrumbLabel') },
        ]}
        actions={
          <>
            <DangerBtn type="button" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <TrashSvg />
              {t('processes.datas.deleteBtn')}
            </DangerBtn>
            <BlueOutlineBtn
              type="button"
              onClick={() => navigate(`/processes/edit/${id}/mapping/${mappingId}`)}
            >
              <EditSvg />
              {t('processes.show.actions.edit')}
            </BlueOutlineBtn>
          </>
        }
      />

      <Wrap>
        {/* Process context card */}
        <ProcCard>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <ProcIconWrap><LayersSvg /></ProcIconWrap>
            <div style={{ flex: 1 }}>
              <ProcLabel>{t('ropa.process.label')}</ProcLabel>
              <ProcName>{process?.name}</ProcName>
              <MetaBadgeWrap>
                {process?.user && (
                  <MetaBadge $bg="color-mix(in srgb, var(--vo-cobalto) 10%, transparent)">
                    <UserSvg />
                    <MetaBadgeText $color="var(--vo-cobalto)">{process.user}</MetaBadgeText>
                  </MetaBadge>
                )}
                {process?.criticity && critText && (
                  <MetaBadge $bg={critStyle.bg}>
                    <MetaBadgeText $color={critStyle.color}>
                      {t('processes.show.fields.criticality')}: {critText}
                    </MetaBadgeText>
                  </MetaBadge>
                )}
                <MetaBadge $bg={process?.dpia ? 'var(--vo-danger-10)' : 'var(--vo-slate-10)'}>
                  <MetaBadgeText $color={process?.dpia ? 'var(--vo-danger)' : 'var(--vo-slate)'}>
                    {t('processes.show.fields.dpia')}: {process?.dpia
                      ? t('processes.list.filters.options.yes')
                      : t('processes.list.filters.options.no')}
                  </MetaBadgeText>
                </MetaBadge>
                <MetaBadge $bg={process?.lia ? 'var(--vo-info-10)' : 'var(--vo-slate-10)'}>
                  <MetaBadgeText $color={process?.lia ? 'var(--vo-celeste)' : 'var(--vo-slate)'}>
                    {t('processes.show.fields.lia')}: {process?.lia
                      ? t('processes.list.filters.options.yes')
                      : t('processes.list.filters.options.no')}
                  </MetaBadgeText>
                </MetaBadge>
              </MetaBadgeWrap>
            </div>
          </div>
          {process?.descriptionDataFlow && (
            <DescBox>
              <DescLabel>{t('ropa.process.dataFlowDescription')}</DescLabel>
              <DescText>{process.descriptionDataFlow}</DescText>
            </DescBox>
          )}
        </ProcCard>

        {/* Dado card */}
        {dado ? (
          <DadoCard>
            <DadoHeader>
              <div>
                <DadoName>{dado.name}</DadoName>
                <CatBadge>{categoryName}</CatBadge>
              </div>
              <TreatCountBadge>
                {t('processes.datas.treatmentCount', { count: treatments.length })}
              </TreatCountBadge>
            </DadoHeader>

            <InfoStrip>
              <InfoCell>
                <InfoLabel>{t('ropa.process.origin')}</InfoLabel>
                <InfoValue>{dado.dataSource || '—'}</InfoValue>
              </InfoCell>
              <InfoCell>
                <InfoLabel>{t('processes.show.dataTable.holder')}</InfoLabel>
                <InfoValue>{dado.holderProfile || '—'}</InfoValue>
              </InfoCell>
              <InfoCell>
                <InfoLabel>{t('ropa.process.retention')}</InfoLabel>
                <InfoValue>{dado.retentionPeriod || '—'}</InfoValue>
              </InfoCell>
              <InfoCell>
                <InfoLabel>{t('ropa.process.storage')}</InfoLabel>
                <InfoValue>{dado.storage || '—'}</InfoValue>
              </InfoCell>
            </InfoStrip>

            {treatments.map((dp, idx) => {
              const tc      = TREAT_COLORS[idx % TREAT_COLORS.length];
              const isShare = !!(dp.shareType || dp.destiny);
              return (
                <TreatSection key={idx}>
                  <TreatHdr $grad={tc.grad}>
                    <ShieldSvg color={tc.text} />
                    <TreatTitle $color={tc.text}>
                      {t('dataMapping.treatmentIndex', { index: idx + 1 })} — {dp.processing}
                    </TreatTitle>
                  </TreatHdr>
                  <TreatGrid $bg={tc.bg}>
                    <TreatCell>
                      <FieldLabel>{t('ropa.process.finality')}</FieldLabel>
                      <FieldValue>{dp.finality || '—'}</FieldValue>
                    </TreatCell>
                    <TreatCell>
                      <FieldLabel>{t('ropa.process.position')}</FieldLabel>
                      <FieldValue>{dp.position || '—'}</FieldValue>
                    </TreatCell>
                    <TreatCell>
                      <FieldLabel>{t('ropa.process.tool')}</FieldLabel>
                      <FieldValue>{dp.tool || '—'}</FieldValue>
                    </TreatCell>

                    <TreatCell>
                      <FieldLabel>{t('ropa.process.legalBase')}</FieldLabel>
                      <FieldValue>{legalBaseByKey.get(dp.legalBase) ?? dp.legalBase ?? '—'}</FieldValue>
                    </TreatCell>

                    {isShare ? (
                      <>
                        <TreatCell>
                          <FieldLabel>{t('ropa.process.shareType')}</FieldLabel>
                          <FieldValue>{dp.shareType || '—'}</FieldValue>
                        </TreatCell>
                        <TreatCell>
                          <FieldLabel>{t('ropa.process.destiny')}</FieldLabel>
                          <FieldValue>{dp.destiny || '—'}</FieldValue>
                        </TreatCell>
                        <TreatCell $full>
                          <FieldLabel>{t('ropa.process.complementLegalBasis')}</FieldLabel>
                          {dp.complementLegalBasis
                            ? <FieldValue>{dp.complementLegalBasis}</FieldValue>
                            : <FieldValueMuted>—</FieldValueMuted>}
                        </TreatCell>
                      </>
                    ) : (
                      <TreatCell $spanTwo>
                        <FieldLabel>{t('ropa.process.complementLegalBasis')}</FieldLabel>
                        {dp.complementLegalBasis
                          ? <FieldValue>{dp.complementLegalBasis}</FieldValue>
                          : <FieldValueMuted>—</FieldValueMuted>}
                      </TreatCell>
                    )}

                    <TreatCell $full $topBorder $borderColor={tc.border}>
                      <FieldLabel>{t('dataMapping.fields.observation')}</FieldLabel>
                      {dp.observation
                        ? <FieldValue>{dp.observation}</FieldValue>
                        : <FieldValueMuted>—</FieldValueMuted>}
                    </TreatCell>
                  </TreatGrid>
                </TreatSection>
              );
            })}

            {treatments.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <p style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: 'var(--vo-slate)', margin: 0 }}>
                  {t('ropa.process.noData')}
                </p>
              </div>
            )}
          </DadoCard>
        ) : (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p style={{ fontFamily: "'Lato', sans-serif", fontSize: 13, color: 'var(--vo-slate)', margin: 0 }}>
              {t('processes.datas.errorLoad')}
            </p>
          </div>
        )}
      </Wrap>
    </Container>
  );
};
